import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, MessageCircle, ClipboardList, Megaphone } from 'lucide-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { NotificationBell } from '@/components/common/NotificationBell';
import { useConversasConsumerNaoLidas } from '@/hooks/useConversasConsumerNaoLidas';
import { cn } from '@/lib/utils';

interface ConsumerLayoutProps {
  children: ReactNode;
  /** Esconde a navegação inferior (ex.: ecrã de chat focado). Sem padding no main. */
  hideNav?: boolean;
  /** Esconde o cabeçalho de topo (identidade + sino). Ex.: página de perfil. */
  hideHeader?: boolean;
}

/**
 * Casco da área do cliente: cabeçalho simples + barra de navegação inferior
 * (mobile-first). Reaproveita o tema/tokens existentes; UI em português via i18n.
 */
export function ConsumerLayout({ children, hideNav = false, hideHeader = false }: ConsumerLayoutProps) {
  const { t } = useTranslation();
  const { consumer } = useAuthContext();
  const inicial = consumer?.nome?.charAt(0)?.toUpperCase() || '?';
  const location = useLocation();
  const mensagensNaoLidas = useConversasConsumerNaoLidas();

  const nav = [
    { to: '/app', icon: Home, label: t('consumer.nav.home') },
    { to: '/app/conversas', icon: MessageCircle, label: t('consumer.nav.messages') },
    { to: '/app/servicos', icon: ClipboardList, label: t('consumer.nav.services') },
    { to: '/app/pedidos', icon: Megaphone, label: t('consumer.nav.requests') },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {!hideHeader && (
      <header className="flex items-center justify-between px-4 h-16 border-b border-border">
        <Link to="/app/perfil" className="ph-user" aria-label={t('consumer.profile.title')}>
          <span className="cli-itm__avt-img" style={{ width: 36, height: 36, fontSize: 14 }}>
            {consumer?.avatar_url ? (
              <img src={consumer.avatar_url} alt={consumer.nome || ''} />
            ) : (
              inicial
            )}
          </span>
          <span className="ph-user__info">
            <span className="ph-user__name">{consumer?.nome || t('common.user')}</span>
            <span className="ph-user__role">{t('consumer.levelLabel', { level: 0 })}</span>
          </span>
        </Link>
        <NotificationBell />
      </header>
      )}

      <main className={cn('flex-1', hideNav ? '' : 'p-4 pb-20')}>{children}</main>

      {!hideNav && (
      <nav className="fixed bottom-0 inset-x-0 h-16 border-t border-border bg-card flex">
        {nav.map(({ to, icon: Icon, label }) => {
          const active = location.pathname === to;
          return (
            <Link
              key={to}
              to={to}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-1 text-xs',
                active ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              <span className="relative">
                <Icon className="h-5 w-5" />
                {to === '/app/conversas' && mensagensNaoLidas > 0 && (
                  <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-foreground text-background text-[10px] font-semibold flex items-center justify-center">
                    {mensagensNaoLidas > 9 ? '9+' : mensagensNaoLidas}
                  </span>
                )}
              </span>
              {label}
            </Link>
          );
        })}
      </nav>
      )}
    </div>
  );
}
